import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiClient } from 'Utils/api';
import { useTranslation } from 'react-i18next';
import Header from 'View/Component/Header/Header';
import Footer from 'View/Component/Footer/Footer';
import FilterSidebar from 'View/Component/FilterSidebar/FilterSidebar';
import WatchCard from 'View/Component/WatchCard/WatchCard';
import Pagination from 'View/Component/Pagination/Pagination';
import * as S from './ShopListingPage.style';
import { useCountry } from 'Context/CountryContext';

// API Response type - matches backend GetWatchesAction response
interface WatchListItem {
  id: string;
  brand: string;
  model: string;
  slug: string;
  priceEur: number;
  priceCents: number;
  status: string;
  referenceNumber?: string | null;
  yearOfProduction?: number | null;
  description?: string | null;
  caseMaterial?: string | null;
  caseDiameterMm?: number | null;
  dialColor?: string | null;
  braceletMaterial?: string | null;
  movement?: string | null;
  caliber?: string | null;
  powerReserveHours?: number | null;
  hasBox?: boolean | null;
  hasPapers?: boolean | null;
  condition?: string | null;
  images?: any[];
}

interface FilterState {
  price_min?: number;
  price_max?: number;
  status?: string;
  brands: string[];
  referenceNumber?: string;
  categories: { [categoryId: string]: string[] };
}

const ShopListingPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { country } = useCountry();
  const [searchParams, setSearchParams] = useSearchParams();
  const [watches, setWatches] = useState<WatchListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 50, totalPages: 0 });

  // Parse filter state from URL search params (shareable / bookmarkable filters)
  const parseFiltersFromUrl = (sp: URLSearchParams): FilterState => {
    const brandsParam = sp.get('brands');
    const availableParam = sp.get('available');
    const priceMin = sp.get('price_min');
    const priceMax = sp.get('price_max');
    const refParam = sp.get('ref');

    const categories: { [k: string]: string[] } = {};
    sp.forEach((value, key) => {
      if (key.startsWith('cat_') && value) {
        const categoryId = key.slice(4);
        const opts = value.split(',').map((v) => v.trim()).filter(Boolean);
        if (opts.length > 0) categories[categoryId] = opts;
      }
    });

    return {
      brands: brandsParam ? brandsParam.split(',').map((b) => b.trim()).filter(Boolean) : [],
      categories,
      ...(refParam ? { referenceNumber: refParam } : {}),
      ...(availableParam === '1' ? { status: 'AVAILABLE' } : {}),
      ...(priceMin ? { price_min: Number(priceMin) } : {}),
      ...(priceMax ? { price_max: Number(priceMax) } : {}),
    };
  };

  const filtersToUrlParams = (f: FilterState): Record<string, string> => {
    const out: Record<string, string> = {};
    if (f.brands.length > 0) out.brands = f.brands.join(',');
    if (f.referenceNumber && f.referenceNumber.trim()) out.ref = f.referenceNumber.trim();
    if (f.status === 'AVAILABLE') out.available = '1';
    if (f.price_min !== undefined) out.price_min = String(f.price_min);
    if (f.price_max !== undefined) out.price_max = String(f.price_max);
    Object.entries(f.categories).forEach(([categoryId, opts]) => {
      if (opts.length > 0) out[`cat_${categoryId}`] = opts.join(',');
    });
    return out;
  };

  // Initialize filters from URL parameters
  const [filters, setFilters] = useState<FilterState>(() => parseFiltersFromUrl(searchParams));

  useEffect(() => {
    // Update filters when URL params change
    const newFilters = parseFiltersFromUrl(searchParams);
    setFilters(newFilters);
    fetchWatches(newFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, i18n.language, country]);

  const fetchWatches = async (filterParams: FilterState = filters) => {
    setLoading(true);
    try {
      const params: any = {
        page: searchParams.get('page') || 1,
        limit: 50,
      };

      // Apply filters from parameter
      if (filterParams.price_min) params.price_min = filterParams.price_min;
      if (filterParams.price_max) params.price_max = filterParams.price_max;
      if (filterParams.status) params.status = filterParams.status;
      if (filterParams.brands.length > 0) params.brands = filterParams.brands;
      if (filterParams.referenceNumber && filterParams.referenceNumber.trim()) {
        params.referenceNumber = filterParams.referenceNumber.trim();
      }
      if (Object.keys(filterParams.categories).length > 0) {
        params.categories = JSON.stringify(filterParams.categories);
      }

      const response = await apiClient.get(`/api/watches`, { params });
      setWatches(response.data.watches || []);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching watches:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters: FilterState) => {
    setFilters(newFilters);
  };

  const handleFilterApply = (appliedFilters: FilterState) => {
    setFilters(appliedFilters);
    // Sync filters into URL so they are shareable and survive reload / back-navigation
    const next = filtersToUrlParams(appliedFilters);
    next.page = '1';
    setSearchParams(next);
  };

  const handlePageChange = (page: number) => {
    // Preserve current filters in URL, only swap page
    const next = filtersToUrlParams(filters);
    next.page = page.toString();
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <Header />
      <S.PageContainer>
        <S.ContentContainer>
          <FilterSidebar
            filters={filters}
            onChange={handleFilterChange}
            onApply={handleFilterApply}
          />

          <S.WatchesContent>
            <S.WatchesHeader>
              <S.PageTitle>{t('shop.page_title')}</S.PageTitle>
              <S.WatchesCount>
                {loading
                  ? t('shop.loading_short')
                  : t('shop.results_count', {
                      from: (pagination.page - 1) * pagination.limit + 1,
                      to: Math.min(pagination.page * pagination.limit, pagination.total),
                      total: pagination.total,
                    })}
              </S.WatchesCount>
            </S.WatchesHeader>

            {loading ? (
              <S.LoadingMessage>{t('shop.loading_watches')}</S.LoadingMessage>
            ) : watches.length === 0 ? (
              <S.NoResults>
                <p>{t('shop.no_results')}</p>
              </S.NoResults>
            ) : (
              <>
                <S.WatchGrid>
                  {watches.map((watch) => (
                    <WatchCard key={watch.id} watch={watch} />
                  ))}
                </S.WatchGrid>

                {pagination.totalPages > 1 && (
                  <Pagination
                    currentPage={pagination.page}
                    totalPages={pagination.totalPages}
                    onPageChange={handlePageChange}
                  />
                )}
              </>
            )}
          </S.WatchesContent>
        </S.ContentContainer>
      </S.PageContainer>
      <Footer />
    </>
  );
};

export default ShopListingPage;
